// Градове › райони — the city_areas rows of one city, edited in place. A polygon is reported,
// never edited: the generator needs names, and leads.area falls back to the aliases.

import { useState } from 'react';
import {
  useCityAreas,
  useDeleteArea,
  useSaveArea,
  type CityAreaInput,
  type CityAreaRow,
} from './useCities.js';
import { arrayToLines, linesToArray } from '../verticals/fields.js';

function emptyArea(cityKey: string): CityAreaInput {
  return {
    id: null,
    city_key: cityKey,
    name: '',
    name_lat: '',
    aliases: [],
    include_in_queries: true,
    search_weight: 1,
  };
}

function AreaRowForm({ initial, onDone }: { initial: CityAreaInput; onDone: () => void }) {
  const save = useSaveArea();
  const [draft, setDraft] = useState(initial);
  const [aliasText, setAliasText] = useState(() => arrayToLines(initial.aliases));
  const set = <K extends keyof CityAreaInput>(field: K, value: CityAreaInput[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  return (
    <tr className="adm__row--dirty">
      <td>
        <input
          type="text"
          className="adm__input"
          value={draft.name}
          onChange={(event) => set('name', event.target.value)}
          placeholder="Лазур"
          aria-label="Име на района"
        />
      </td>
      <td>
        <input
          type="text"
          className="adm__input"
          value={draft.name_lat}
          onChange={(event) => set('name_lat', event.target.value)}
          placeholder="Lazur"
          aria-label="Име на латиница"
        />
      </td>
      <td>
        <textarea
          className="adm__textarea adm__textarea--cell"
          rows={2}
          value={aliasText}
          onChange={(event) => {
            setAliasText(event.target.value);
            set('aliases', linesToArray(event.target.value));
          }}
          placeholder={'ж.к. Лазур\nЛазур'}
          aria-label="Други изписвания"
        />
      </td>
      <td>
        <input
          type="checkbox"
          className="adm__toggle-check"
          checked={draft.include_in_queries}
          onChange={(event) => set('include_in_queries', event.target.checked)}
          aria-label="В заявките"
        />
      </td>
      <td>
        <input
          type="number"
          className="adm__input adm__input--mono adm__input--tiny"
          min={1}
          max={5}
          value={draft.search_weight}
          onChange={(event) => set('search_weight', Number(event.target.value) || 1)}
          aria-label="Тежест"
        />
      </td>
      <td>—</td>
      <td className="adm__cell-actions">
        <button
          type="button"
          className="adm__resolve-btn"
          disabled={draft.name.trim() === '' || save.isPending}
          onClick={() => save.mutate(draft, { onSuccess: onDone })}
        >
          Запази
        </button>
        <button type="button" className="adm__json-btn" onClick={onDone}>
          Отказ
        </button>
        {save.isError ? <span className="adm__err">{(save.error as Error).message}</span> : null}
      </td>
    </tr>
  );
}

function AreaRow({ area, onEdit }: { area: CityAreaRow; onEdit: () => void }) {
  const remove = useDeleteArea();
  return (
    <tr className={area.include_in_queries ? '' : 'adm__row--off'}>
      <td>
        <strong>{area.name}</strong>
      </td>
      <td>{area.name_lat || '—'}</td>
      <td className="adm__sub">{area.aliases.join(', ') || '—'}</td>
      <td>{area.include_in_queries ? 'да' : 'не'}</td>
      <td className="adm__num">{area.search_weight}</td>
      <td>{area.has_polygon ? '✓' : '—'}</td>
      <td className="adm__cell-actions">
        <button type="button" className="adm__json-btn" onClick={onEdit}>
          Редакция
        </button>
        <button
          type="button"
          className="adm__json-btn adm__json-btn--danger"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(`Изтриване на район „${area.name}“?`)) {
              remove.mutate({ id: area.id, city_key: area.city_key });
            }
          }}
        >
          Изтрий
        </button>
        {remove.isError ? (
          <span className="adm__err">{(remove.error as Error).message}</span>
        ) : null}
      </td>
    </tr>
  );
}

export function AreasTable({ cityKey }: { cityKey: string }) {
  const { data: areas = [], isLoading } = useCityAreas(cityKey);
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  if (isLoading) return <p className="adm__msg">Зареждане на районите…</p>;

  return (
    <div className="adm__areas">
      <div className="adm__section-header">
        <h3 className="adm__h">Райони ({areas.length})</h3>
        <span className="adm__sub">
          Районите с отметка „в заявките“ влизат в матрицата; тежест 2 носи и заявки по услуги.
        </span>
      </div>
      <div className="adm__tablewrap">
        <table className="adm__table">
          <thead>
            <tr>
              <th>район</th>
              <th>латиница</th>
              <th>други изписвания</th>
              <th>в заявките</th>
              <th className="adm__num">тежест</th>
              <th>полигон</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {areas.map((area) =>
              editing === area.id ? (
                <AreaRowForm key={area.id} initial={{ ...area }} onDone={() => setEditing(null)} />
              ) : (
                <AreaRow key={area.id} area={area} onEdit={() => setEditing(area.id)} />
              ),
            )}
            {editing === 'new' ? (
              <AreaRowForm key="new" initial={emptyArea(cityKey)} onDone={() => setEditing(null)} />
            ) : null}
          </tbody>
        </table>
      </div>
      {editing === 'new' ? null : (
        <div className="adm__actions">
          <button type="button" className="adm__json-btn" onClick={() => setEditing('new')}>
            + Район
          </button>
        </div>
      )}
    </div>
  );
}
